import { getProxyUrl } from '@proxies/index'
import {
  GLBClientData,
  Getter,
  Hole,
  HolesID,
  Partition,
  PartitionID,
  Setter,
  Store,
  Walls,
  WallsID,
} from '@types'
import { quatYToDeg } from '@utils/conversion/quatYToDeg'
import { toVector3 } from '@utils/conversion/toVector3'
import decomposeMatrix from '@utils/decomposeMatrix'
import { radians } from '@utils/generalMaths/geometry'
import isHole from '@utils/sceneObjects/assertions/isHole'
import isPartition from '@utils/sceneObjects/assertions/isPartition'
import isWall from '@utils/sceneObjects/assertions/isWall'
import { getHolePosition } from '@utils/sceneObjects/holes/getHolePosition'
import { getGLBSize } from '@utils/sceneObjects/items/getGLBSize'
import { newGetDataFromGLB } from '@utils/sceneObjects/items/newGetDataFromGLB'
import { setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import { Box3, Euler, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
export type PositionType = 'Interior' | 'Exterior' | 'Dentro' | null

export const createHoleActions = (set: Setter, get: Getter) => ({
  editHolePosition: (
    holeId: HolesID,
    wallID: WallsID,
    distI: number,
    distD: number,
    bisagra = 'Dentro' as PositionType,
    mano?: Hole['mano'],
  ) => {
    set((store) => {
      const hole = store.items.get(holeId) as Hole
      const wall = store.items.get(wallID) as Walls

      if (!hole || !wall || !hole?.textures || !hole?.matrix) {
        return console.warn(`Editing Not hole or wall found: ${hole} ${wall}`, {
          hole,
          wall: { ...wall },
          holeId,
          wallID,
        })
      }
      hole.wall = wallID
      hole.distI = distI
      hole.distD = distD
      const rotation = wall.rotation
      hole.matrix.makeRotationFromEuler(new Euler(0, -rotation, 0, 'XYZ'))
      hole.bisagra = bisagra
      hole.mano = mano
      const newPos = getHolePosition(hole, wall, bisagra)
      hole.matrix.setPosition(newPos)
    })
  },
  editHolePositionIfPartition: (
    holeId: HolesID,
    partitionID: PartitionID,
    distI: number,
    distD: number,
    bisagra = 'Dentro' as PositionType,
    mano?: Hole['mano'],
  ) => {
    set((store) => {
      const hole = store.items.get(holeId) as Hole
      const partition = store.items.get(partitionID) as Partition

      if (!hole || !partition || !hole?.textures || !hole?.matrix) {
        return console.warn(`Editing Not hole or partition found: ${hole} ${partition}`, {
          hole,
          partition: { ...partition },
          holeId,
          partitionID,
        })
      }
      hole.wall = partitionID
      hole.distI = distI
      hole.distD = distD
      const rotation = decomposeMatrix(partition.matrix).rotation
      const rot = radians(quatYToDeg(rotation))
      hole.matrix.makeRotationFromEuler(new Euler(0, rot, 0, 'XYZ'))
      hole.bisagra = bisagra
      hole.mano = mano
      const newPos = getHolePosition(hole, partition, bisagra)
      hole.matrix.setPosition(newPos)
    })
  },
  drawHole: async (
    id: string,
    _idUnidades: number,
    options?: { mano: Hole['mano']; bisagra: Hole['bisagra'] },
    img?: string,
  ) => {
    const store = get()
    const glbInScene = get().sceneGLB
    const idUnidades = Number(_idUnidades)

    let backGLBInfo, glb, info

    if (glbInScene.has(idUnidades)) {
      const { backGLBInfo: back, glb: _glb, info: _info } = glbInScene.get(idUnidades)
      glb = _glb
      backGLBInfo = back
      info = _info
    } else {
      const params = {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept-Language': 'ES',
          'Content-Type': 'application/json',
          Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
        },
      } satisfies RequestInit

      const [glbDataPre, dataPre] = await Promise.all([
        fetch(getProxyUrl(`api/Unidades/getunidad/${idUnidades}?not-from-cache-please`), params),
        fetch(getProxyUrl(`api/Unidades/getinfopuertaventa/${idUnidades}`), params),
      ])

      backGLBInfo = await glbDataPre.json()
      const loader = await setGLTFLoader()
      glb = await loader.loadAsync(backGLBInfo.referenciaBlender)
      info = await dataPre.json()
    }

    if (info == null) {
      const params = {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept-Language': 'ES',
          'Content-Type': 'application/json',
          Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
        },
      } satisfies RequestInit

      const dataPre = await fetch(
        getProxyUrl(`api/Unidades/getinfopuertaventa/${idUnidades}`),
        params,
      )
      info = await dataPre.json()
    }

    const hole = newGetDataFromGLB({
      glbData: glb,
      id,
      backGLBInfo,
      type: 'holes',
      mano: options?.mano,
      catalogID: backGLBInfo.catalogID ?? '',
      distI: 0,
      distD: 0,
      bisagra: options?.bisagra,
      combinaciones: info?.combinaciones,
      offset: backGLBInfo.offsetVentanasPuertas ?? 0,
      idUnidades,
      visible: true,
      img,
      isBlocked: false,
      increment: store.increment - 1,
      ...info,
      extraInfo: {
        universal: true,
        price: 0,
        visible: true,
        url: '',
      },
    })

    set((store) => {
      store.sceneGLB.set(idUnidades, { glb, backGLBInfo, info })
      store.items.set(hole.id, hole)
      store.layers[store.currentLayer].items.add(hole.id)
      store.layers[store.currentLayer].selected.clear()
      store.layers[store.currentLayer].selected.add(hole.id)
      store.currentObjectID = hole.id
    })
  },
  editHoleDist: (id: string) => {
    set((store) => {
      const hole = store.items.get(id) as Hole
      if (!hole) return
      const wall = store.items.get(hole.wall)
      if (!wall) return
      const positionType = hole.bisagra
      const newPos = getHolePosition(hole, wall, positionType)
      hole.matrix.setPosition(newPos)
    })
  },
  editDist: (type: 'distD' | 'distI', id: string, value: number) => {
    set((store) => {
      const hole = store.items.get(id) as Hole
      if (!hole) return console.log('Hole not found')
      const width = hole.width
      const wallID = hole.wall
      const wall = store.items.get(wallID)
      const numWallWidth = wall.width
      if (value === '0' || value === '' || isNaN(value)) {
        if (!width && width !== 0) return
        switch (type) {
          case 'distI': {
            hole.distI = 0
            hole.distD = numWallWidth - width
            return
          }
          case 'distD': {
            hole.distI = numWallWidth - width
            hole.distD = 0
            return
          }
        }
      }
      switch (type) {
        case 'distI': {
          // S|---\---Item-\------|E
          // Cases: distI+itemWidth > wallSize
          //distI = wallWidth-width
          if (value >= numWallWidth - width) {
            hole.distI = numWallWidth - width
            hole.distD = 0
          } else {
            hole.distI = value
            hole.distD = numWallWidth - value - width
          }
          break
        }
        case 'distD':
          // S|---\---Item-\------|E
          // Cases: distD-itemWidth <0
          if (value + width > numWallWidth) {
            if (width >= numWallWidth) {
              hole.distD = 0
              break
            }
            hole.distI = 0
            hole.distD = numWallWidth - width
          } else {
            hole.distI = numWallWidth - value - width
            hole.distD = value
          }
          break
        default:
          break
      }
    })
  },
  editHole: (id: string, config: Partial<Hole>) => {
    set((store) => {
      const hole = store.items.get(id)
      if (!isHole(hole)) return
      for (const [property, value] of Object.entries(config)) {
        hole[property] = value
      }
    })
  },
  updateHole: (id: string, desiredThickness?: number) => {
    set((store) => {
      const hole = store.items.get(id) as Hole
      if (!hole || !hole?.morphs || !hole?.matrix) {
        return console.warn(`Alert: Updating: Not hole found: ${hole} `)
      }
      const wall = store.items.get(hole.wall)
      if (!isWall(wall) && !isPartition(wall))
        return console.log('Wall not found on the hole: ', { wall, holeID: id })
      const positionType = hole.bisagra
      let rot
      if (!isWall(wall)) {
        const rotation = decomposeMatrix(wall.matrix).rotation
        rot = radians(quatYToDeg(rotation))
      } else {
        rot = -wall.rotation
      }

      hole.matrix.makeRotationFromEuler(new Euler(0, rot, 0, 'XYZ'))
      if (wall.width < hole.width + hole.distI) {
        hole.distI = wall.width - hole.width
        hole.distD = 0
      }
      if (hole.distI < 0) {
        hole.distI = 0
      }
      const textures = hole.morphs
      if (hole.tipoUnidNorm === 'P') {
        textures.forEach((el, i) => {
          if (el.name === 'Fondo' && hole.bisagra !== null) {
            hole.morphs[i].default = wall.thickness + hole.offset
            el.default = wall.thickness + hole.offset
          }
        })
      }
      const newPos = getHolePosition(hole, wall, positionType, desiredThickness)
      hole.matrix.setPosition(newPos)
      hole.distD = wall.width - hole.width - hole.distI
    })
  },
  moveInsideWall: (id: string, newPosition: Vector3) => {
    set((store: Store) => {
      const hole = store.items.get(id)
      if (hole && hole.type === 'holes') {
        const wall = store.items.get(hole.wall)
        if ((wall && wall.type === 'walls') || (wall && wall.type === 'partition')) {
          const end = toVector3(wall.end)
          const start = toVector3(wall.start)
          const ESv = end.clone().sub(start)
          const PEv = newPosition.clone().sub(start)
          const projectedVector = PEv.clone().projectOnVector(ESv)

          let length = projectedVector.length()
          let isValid = true

          if (length < 0) {
            length = 0
          }
          if (length > wall.width || length + hole.width > wall.width) {
            hole.distI = wall.width - hole.width
            hole.distD = 0
            return
          } else {
            wall.holes.forEach((_hole) => {
              const holeHole = store.items.get(_hole)
              if (!isHole(holeHole) || holeHole.id === id || holeHole.wall !== wall.id) return
              if (length >= holeHole.distI && length <= holeHole.distI + holeHole.width) {
                // We are inside, default snap to the right unless:
                if (holeHole.distI + holeHole.width + hole.width > wall.width) {
                  // can't be snapped to right so snap to left
                  hole.distI = holeHole.distI - hole.width
                  hole.distD = holeHole.distD
                  isValid = false
                  return
                } else {
                  // Can't snap anyway so just don't move
                  isValid = false
                }

                hole.distI = holeHole.distI + holeHole.width
                hole.distD = holeHole.distI + holeHole.width + hole.width
                isValid = false
                return
              }
              if (length + hole.width >= holeHole.distI && length <= holeHole.distI) {
                // This checks whether you can snap on the left of the item.
                if (holeHole.distI - hole.width < 0) {
                  // Can't snap on the left, so snap on the right
                  hole.distI = holeHole.distI + holeHole.width
                  hole.distD = wall.width - (holeHole.distI + holeHole.width + hole.width)
                  isValid = false
                  return
                }
                // snap on the left of the item
                hole.distI = holeHole.distI - hole.width
                hole.distD = holeHole.distI
                isValid = false
              }
            })
          }
          if (isValid) {
            hole.distI = length
            hole.distD = wall.width - (length + hole.width)
          }
        }
      }
    })
  },
  swapGlb: async (id: string, newID?: number) => {
    const params = {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Accept-Language': 'ES',
        'Content-Type': 'application/json',
        Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
      },
    } satisfies RequestInit
    if (!newID) return

    const currentHole = get().items.get(id)
    const originalImage = currentHole?.image

    const glbDataPre = (await fetch(
      getProxyUrl(`api/Unidades/getunidad/${newID}?not-from-cache-please`),
      params,
    )) as unknown as GLBClientData
    const glbData = await glbDataPre.json()
    const referenciaBlender = glbData?.referenciaBlender

    const loader = await setGLTFLoader()
    loader.load(referenciaBlender, (glb) => {
      const glbSize = getGLBSize(glb.scene)
      const boundingBox = new OBB().fromBox3(new Box3().setFromObject(glb.scene))

      set((store) => {
        store.sceneGLB.set(newID, { glb, backGLBInfo: glbData })
        store.sceneBoundingBoxes.set(id, { type: 'holes', boundingBox })
        const hole = store.items.get(id)
        if (!isHole(hole)) return
        hole.glbSize = glbSize
        // Usar la nueva imagen si está disponible, sino mantener la original OJO
        hole.image = glbData?.referenciaJpgpng ?? originalImage ?? 'Suelo'
        hole.catalogID = newID
        hole.idUnidades = newID
        hole.offset = glbData.offsetVentanasPuertas ?? 0
        store.currentObjectID = hole.id
      })
    })
  },
})
